import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Bell, Mail, ArrowRight, Stamp, PenLine, Hash, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import FilmMark from "@/components/FilmMark";
import BoutiqueCheckoutModal from "@/components/BoutiqueCheckoutModal";
import ProductShare from "@/components/ProductShare";
import { useLang } from "@/i18n/LanguageContext";

function ProvisionalBadge({ en }) {
  return (
    <span className="absolute top-3 left-3 z-10 bg-bg/90 border border-fg/20 px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] uppercase text-fg/70">
      {en ? "Provisional visual" : "Visuel provisoire"}
    </span>
  );
}

export default function BoutiquePage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [products, setProducts] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/boutique/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const repros = products.filter((p) => p.type === "reproduction");
  const goodies = products.filter((p) => p.type === "goodie");

  return (
    <main className="pt-32 pb-24" data-testid="boutique-page">
      <FilmMark />

      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="meta-label text-cinabre">§ {en ? "The Fil Shop · 2026 Edition" : "La Boutique du Fil · Édition 2026"}</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.88] tracking-tight mt-8">
          {en ? <>The <em className="italic">Fil</em><br />Shop.</> : <>La <em className="italic">Boutique</em><br />du Fil.</>}
        </h1>
        <p className="mt-10 font-serif italic text-2xl md:text-3xl leading-[1.35] text-fg/75 max-w-3xl">
          {en ? "Take home a piece of the canal — an artwork, an object, a souvenir of the 30 years. Secure online payment, home delivery." : "Emporter un morceau du canal — une œuvre, un objet, un souvenir des 30 ans. Paiement en ligne sécurisé, livraison à domicile."}
        </p>
      </section>

      {/* Édition 2026 — reproductions numérotées */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="boutique-reproductions">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "2026 Edition · Artwork reproductions" : "Édition 2026 · Reproductions d'œuvres"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>30 copies.<br /><em className="italic">Not one more.</em></> : <>30 exemplaires.<br /><em className="italic">Pas un de plus.</em></>}
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? <>This year, every artwork exhibited on the Thread is issued in <strong>30 copies numbered from 1 to 30</strong> and <strong>signed on the back</strong> by the artist. Your number is assigned when you order — first come, first numbered.</> : <>Cette année, chaque œuvre exposée sur le Fil est éditée en <strong>30 exemplaires numérotés de 1 à 30</strong> et <strong>signés au dos</strong> par l'artiste. Votre numéro vous est attribué à la commande — premier arrivé, premier numéroté.</>}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-px bg-fg/15 border border-fg/15 mb-12 max-w-3xl">
          {[
            { Icon: Hash, l: en ? "Numbered 1 → 30" : "Numérotés 1 → 30" },
            { Icon: PenLine, l: en ? "Signed on the back" : "Signés au dos" },
            { Icon: Stamp, l: en ? "Edition certificate" : "Certificat d'édition" },
          ].map(({ Icon, l }) => (
            <div key={l} className="bg-bg p-5 md:p-6 flex items-center gap-3">
              <Icon size={18} strokeWidth={1.3} className="text-cinabre shrink-0" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/70">{l}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {repros.map((r, i) => {
            const soldOut = r.remaining === 0;
            return (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.06 }}
                id={r.id}
                className="group scroll-mt-32"
                data-testid={`boutique-repro-${i}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden border border-fg/15 bg-fg/5">
                  <ProvisionalBadge en={en} />
                  <img
                    src={r.image}
                    alt={`${r.name} — ${r.artist}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                  />
                  <div className="absolute bottom-3 right-3 bg-fg text-bg px-3 py-1.5 font-mono text-[10px] tracking-[0.2em]" data-testid={`repro-remaining-${i}`}>
                    {soldOut ? (en ? "Sold out" : "Épuisé") : `${r.remaining}/${r.edition_total} ${en ? "available" : "disponibles"}`}
                  </div>
                </div>
                <div className="mt-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight">{r.artist}</h3>
                    <div className="font-mono text-lg tabular-nums shrink-0">{(r.price / 100).toFixed(0)} €</div>
                  </div>
                  <div className="mt-1 font-serif italic text-fg/70">{r.name}</div>
                  <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">
                    {en ? "Edition of 30 · numbered & signed on the back" : "Édition 30 ex. · numérotés & signés au dos"}
                  </div>
                  <button
                    type="button"
                    disabled={soldOut}
                    onClick={() => setBuying(r)}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-fg/25 px-6 py-3 font-mono text-[10px] tracking-[0.24em] uppercase hover:bg-cinabre hover:border-cinabre hover:text-bg transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    data-testid={`buy-repro-${i}`}
                  >
                    <ShoppingBag size={13} /> {soldOut ? (en ? "Edition sold out" : "Édition épuisée") : (en ? "Buy" : "Acheter")}
                  </button>
                  <ProductShare product={r} />
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Les 30 ans — casquettes, t-shirts, gourde */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="boutique-goodies">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Anniversary collection" : "Collection anniversaire"}</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6">
              {en ? <>The <em className="italic">30 years</em>,<br />to wear.</> : <>Les <em className="italic">30 ans</em>,<br />à porter.</>}
            </h2>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-fg/80 leading-relaxed text-lg">
              {en ? "Caps, t-shirts and bottles embroidered with the anniversary '30' — available at the openings, and now online." : "Casquettes, t-shirts et gourdes brodés du « 30 » anniversaire — à retrouver aux vernissages, et dès maintenant en ligne."}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {goodies.map((g, i) => (
            <motion.article
              key={g.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.06 }}
              id={g.id}
              className="group scroll-mt-32"
              data-testid={`boutique-goodie-${i}`}
            >
              <div className="relative aspect-square overflow-hidden border border-fg/15 bg-fg/5">
                <ProvisionalBadge en={en} />
                <img
                  src={g.image}
                  alt={g.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                />
              </div>
              <div className="mt-5">
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-serif text-2xl leading-tight tracking-tight">{g.name}</h3>
                  <div className="font-mono text-lg tabular-nums shrink-0">{(g.price / 100).toFixed(0)} €</div>
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">{g.detail}</div>
                <button
                  type="button"
                  onClick={() => setBuying(g)}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-fg/25 px-6 py-3 font-mono text-[10px] tracking-[0.24em] uppercase hover:bg-cinabre hover:border-cinabre hover:text-bg transition-colors"
                  data-testid={`buy-goodie-${i}`}
                >
                  <ShoppingBag size={13} /> {en ? "Buy" : "Acheter"}
                </button>
                <ProductShare product={g} />
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Note prix + contact */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto" data-testid="boutique-cta">
        <div className="grid md:grid-cols-12 gap-10 border border-fg/15 p-8 md:p-16">
          <div className="md:col-span-7">
            <div className="meta-label text-cinabre">§ {en ? "Good to know" : "Bon à savoir"}</div>
            <h3 className="font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight mt-4">
              {en ? <>Launch prices, <em className="italic">limited editions.</em></> : <>Prix de lancement, <em className="italic">éditions limitées.</em></>}
            </h3>
            <p className="mt-6 text-fg/75 leading-relaxed max-w-xl">
              {en ? "Visuals are provisional — every order is confirmed by email, with your copy number for art prints. Delivery in France and Europe. Proceeds support the artists and the Fil du Midi." : "Les visuels sont provisoires — chaque commande est confirmée par email avec votre numéro d'exemplaire pour les tirages d'art. Livraison en France et en Europe. Les recettes soutiennent les artistes et le Fil du Midi."}
            </p>
          </div>
          <div className="md:col-span-5 self-center flex flex-col gap-3">
            <Link to="/#newsletter" className="btn-editorial" data-testid="boutique-newsletter">
              <Bell size={14} /> {en ? "Follow the news" : "Suivre les nouveautés"}
            </Link>
            <Link to="/contact" className="btn-editorial ghost" data-testid="boutique-contact">
              <Mail size={14} /> {en ? "A question? Write to us" : "Une question ? Écrivez-nous"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {buying && <BoutiqueCheckoutModal product={buying} onClose={() => setBuying(null)} />}
    </main>
  );
}
